import React, { FC } from 'react'

import { Chip, Stack, useTheme } from '@mui/material'
import { Currency, getCurrencySymbol } from '@caterdesk/utils--money'

import { useTranslation } from 'react-i18next'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'

const canteenSubsidyAndBalanceCurrentMenuDataFragment = graphql(`
  fragment CanteenSubsidyAndBalance_CurrentMenuDataFragment on CCMealService {
    availableSubsidy
  }
`)

const canteenSubsidyAndBalanceUserDataFragment = graphql(`
  fragment CanteenSubsidyAndBalance_UserDataFragment on User {
    wallet {
      balance
    }
    subsidySettings {
      unlimitedSubsidy
    }
  }
`)

type Props = {
  currency: string
  menuData?: FragmentType<typeof canteenSubsidyAndBalanceCurrentMenuDataFragment>
  userData: FragmentType<typeof canteenSubsidyAndBalanceUserDataFragment>
}

const SubsidyAndBalance: FC<Props> = ({
  menuData: menuDataProp,
  userData: userDataProp,
  currency,
}) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const menuData = useFragment(canteenSubsidyAndBalanceCurrentMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenSubsidyAndBalanceUserDataFragment, userDataProp)

  const subsidy = menuData?.availableSubsidy || 0

  return (
    <Stack width="100%" direction="row" flexWrap="nowrap">
      <Chip
        color="default"
        sx={{
          backgroundColor: palette.inverted.light,
          borderTopRightRadius: '0',
          borderBottomRightRadius: '0',
          '& .MuiChip-label': {
            paddingRight: 1,
          },
        }}
        icon={
          currency ? (
            <Chip
              sx={{
                '& .MuiChip-label': {
                  overflow: 'visible',
                  paddingRight: '9px',
                },
              }}
              size="small"
              color="primary"
              variant="filled"
              label={getCurrencySymbol(currency as Currency) ?? '¤'}
            />
          ) : undefined
        }
        label={numberFormat(userData.wallet.balance)}
      />
      <Chip
        sx={{
          borderTopLeftRadius: '0',
          borderBottomLeftRadius: '0',
          '& .MuiChip-label': {
            overflow: 'hidden',
            paddingLeft: 0.5,
          },
          width: '100%',
        }}
        color="success"
        label={
          userData.subsidySettings.unlimitedSubsidy
            ? t('cc:unlimited')
            : `+ ${numberFormat(subsidy)} ${t('cc:free')}`
        }
      />
    </Stack>
  )
}

export default SubsidyAndBalance
